import type { QueryClient } from "@tanstack/react-query";

/**
 * The dashboard status and streak queries, matched against the first element of
 * an Orval query key (`["/v1/nurseries/<id>/<resource>", params]`).
 *
 * Every write that resolves a piece of work — completing a compliance instance,
 * signing off an incident, completing or signing off a follow-up action — moves
 * the dashboard status that drives the summary cards, the priority list and
 * Today's status. It is always added, so a domain helper only declares the paths
 * peculiar to it and cannot omit this one.
 *
 * Matching is by substring rather than on the full path (unlike
 * `isDashboardPageDataKey`, which is deliberately exact and deliberately
 * excludes the status and streak queries) because we want every params variant.
 * The status key embeds ISO `periodStart`/`periodEnd` derived from the page's
 * period selector, and callers' own keys embed component state such as `scope`,
 * `sortBy`, `limit` and `offset` — none of it reconstructable from a mutation
 * site, so a path predicate is the only reliable way to reach them.
 *
 * There is no compile-time link to the generated keys; the drift-guard tests in
 * this module's spec and in each domain helper's spec assert them against the
 * real generated key functions.
 */
const DASHBOARD_KEY_PATH = "/dashboard";

/**
 * The blast radius a domain helper adds on top of the dashboard queries.
 *
 * `queryKeys` exists for hand-rolled hooks that use their own array key rather
 * than a request path, and so cannot be reached by a path predicate at all.
 */
export interface DashboardInvalidationScope {
  keyPaths?: readonly string[];
  queryKeys?: readonly (readonly unknown[])[];
}

/**
 * True when a React Query key belongs to a dashboard query, or to one of the
 * caller's own affected resources. Exported for the drift-guard tests.
 */
export function isDashboardAffectedQueryKey(
  queryKey: readonly unknown[],
  extraKeyPaths: readonly string[] = [],
): boolean {
  const key = queryKey[0];
  if (typeof key !== "string") return false;
  if (key.includes(DASHBOARD_KEY_PATH)) return true;
  return extraKeyPaths.some((path) => key.includes(path));
}

/**
 * Invalidate the dashboard queries plus everything in `scope`.
 *
 * `refetchType: "all"` matters throughout: the app's QueryClient disables
 * `refetchOnMount`, so a merely stale-marked inactive query would keep serving
 * old data forever after remount.
 *
 * The returned promise resolves only once the refetches it triggered have
 * settled — including queries with no observers, which is what lets a caller
 * navigate straight afterwards and find the destination's cache already warm
 * rather than flashing pre-write rows. Two bounds on that: a query absent from
 * the cache matches nothing (harmless, since a cache miss always fetches on
 * mount), and offline fetches are paused rather than awaited, so this never
 * stalls a navigation.
 */
export async function invalidateDashboardAffectedQueries(
  queryClient: QueryClient,
  scope: DashboardInvalidationScope = {},
): Promise<void> {
  const { keyPaths = [], queryKeys = [] } = scope;
  await Promise.all([
    queryClient.invalidateQueries({
      predicate: (query) =>
        isDashboardAffectedQueryKey(query.queryKey, keyPaths),
      refetchType: "all",
    }),
    ...queryKeys.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey, refetchType: "all" }),
    ),
  ]);
}
